// グローバル状態 — Svelte 5 runes ベースのリアクティブストア。
//
// 利用イメージ:
//   import { appState, initState, activeRoulette } from '$lib/state.svelte';
//   await initState();
//   const r = activeRoulette();   // 派生
//
// 状態変更後は persister.markDirty() が自動的に呼ばれる（mutation ヘルパ経由）。

import type { AppPreferences, AppState, EffectKey, Item, ItemSet, Roulette, RouletteId, SetId, SpinEffectSettings, ThemeMode } from './types';
import { makeInitialAppState, makeItem, makeRoulette, makeSet } from './defaults';
import { enforceInvariants } from './invariants';
import { loadState } from './db';
import { createPersister, type Persister } from './persistence';
import { SpinEngine, type SpinPhase } from './spinEngine';
import { buildLayoutForSet, generateRandomSplitOrder } from './wheelLayout';
import { soundManager } from './soundManager';
import { EffectScheduler } from './effectScheduler';
import { uuid } from './uuid';

/** CharacterEffect / MiniCharacterEffect に渡すキャラ種別 */
export type CharType = 'dragon' | 'rabbit' | 'ghost';

/** 永続化しないグローバル UI 状態 (プレビュー再生中フラグ等) */
export const transientState = $state({ effectPreviewActive: false });

// ─────────────────────────────────────────────────────────
//  リアクティブ状態
// ─────────────────────────────────────────────────────────

export const appState = $state<AppState>(makeInitialAppState());

let persister: Persister | null = null;
let initialized = false;

// ─────────────────────────────────────────────────────────
//  初期化
// ─────────────────────────────────────────────────────────

export async function initState(): Promise<void> {
  if (initialized) return;
  const stored = await loadState();
  const initial = stored ? enforceInvariants(stored) : makeInitialAppState();
  // 既存 reactive オブジェクトに上書きするため Object.assign を使う
  Object.assign(appState, initial);
  appState.roulettes = initial.roulettes;
  persister = createPersister(() => snapshot());
  initialized = true;
}

/** 永続化用の plain object スナップショットを返す。 */
function snapshot(): AppState {
  return JSON.parse(JSON.stringify(appState)) as AppState;
}

/** 状態変更後に呼び出す。debounce 自動保存される。 */
export function notifyChange(): void {
  enforceInvariants(appState);
  persister?.markDirty();
}

/** 強制保存（離脱直前など）。 */
export async function flushState(): Promise<void> {
  await persister?.flush();
}

// ─────────────────────────────────────────────────────────
//  JSON エクスポート / インポート (scope §8.3)
// ─────────────────────────────────────────────────────────

/** 現在の AppState を整形済み JSON 文字列にエクスポート */
export function exportAppStateJson(): string {
  return JSON.stringify(snapshot(), null, 2);
}

/** JSON 文字列を AppState としてインポート (現在の状態を上書き)。
 *  成功時 true、形式不正・読み込み失敗時 false。 */
export async function importAppStateJson(json: string): Promise<boolean> {
  try {
    const parsed = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object') return false;
    if (!Array.isArray(parsed.roulettes) || parsed.roulettes.length === 0) {
      return false;
    }
    // 既存ランタイムを全クリーンアップ (古い engine が残らないように)
    for (const id of Array.from(runtimes.keys())) {
      cleanupRuntime(id);
    }
    const validated = enforceInvariants(parsed as AppState);
    Object.assign(appState, validated);
    appState.roulettes = validated.roulettes;
    appState.activeRouletteId = validated.activeRouletteId;
    appState.ui = validated.ui;
    notifyChange();
    await flushState();
    return true;
  } catch {
    return false;
  }
}

// ─────────────────────────────────────────────────────────
//  派生ヘルパ（Svelte 5 では $derived で利用想定）
// ─────────────────────────────────────────────────────────

export function activeRoulette(): Roulette {
  const r = appState.roulettes.find((x) => x.id === appState.activeRouletteId);
  if (!r) {
    // enforceInvariants 済みなら通常到達しない
    throw new Error(`activeRouletteId not found: ${appState.activeRouletteId}`);
  }
  return r;
}

// ─────────────────────────────────────────────────────────
//  RouletteRuntime: 各ルーレットの実行時状態 (回転角・SpinPhase・SpinEngine)
//  グリッド表示で複数ルーレットが独立してスピンするための土台
// ─────────────────────────────────────────────────────────

export class RouletteRuntime {
  wheelAngle = $state(0);
  spinPhase = $state<SpinPhase>('idle');
  winnerText = $state('');
  winnerVisible = $state(false);

  // 演出フラグ (App.svelte が読んで視覚効果を制御)
  // 各演出は variant (1〜5) で見た目/動きを切り替える
  effectFlash = $state(false);
  effectFlashVariant = $state(1);
  effectTextChance = $state(false);
  effectTextChanceVariant = $state(1);
  effectWheelGlow = $state(false);
  effectWheelGlowVariant = $state(1);
  // ミニキャラ (ホイール周辺、ホイール幅 1/4)
  effectMiniChar = $state<CharType | null>(null);
  effectMiniCharVariant = $state(1);
  // カットイン (上下黒帯式、画面中央高さ)
  effectCutInChar = $state<CharType | null>(null);
  effectCutInVariant = $state(1);

  engine: SpinEngine;
  private scheduler = new EffectScheduler();

  /** 各 EffectKey の発火コールバック (scheduler 用 + previewEffect 用に共有)
   *  variant: 1〜5 で固定再生、省略/0/undefined でランダム抽選 (1〜5) */
  private effectCallbacks(variant?: number) {
    /** variant 解決: 0/undefined ならランダム 1〜5 */
    const v = (): number => (variant && variant >= 1 && variant <= 5)
      ? variant
      : 1 + Math.floor(Math.random() * 5);
    return {
      soundConfirm:    () => soundManager.playEffect('confirm', variant),
      soundExpect:     () => soundManager.playEffect('expect', variant),
      soundNgConfirm:  () => soundManager.playEffect('ng', variant),
      miniCharTarget:  () => this.fireMiniChar('dragon', v()),
      miniCharExpect:  () => this.fireMiniChar('rabbit', v()),
      miniCharNg:      () => this.fireMiniChar('ghost', v()),
      cutInTarget:     () => this.fireCutIn('dragon', v()),
      cutInExpect:     () => this.fireCutIn('rabbit', v()),
      cutInNg:         () => this.fireCutIn('ghost', v()),
      flashConfirm: () => {
        this.effectFlashVariant = v();
        this.effectFlash = true;
        setTimeout(() => { this.effectFlash = false; }, 1100);
      },
      wheelGlow: () => {
        this.effectWheelGlowVariant = v();
        this.effectWheelGlow = true;
        setTimeout(() => { this.effectWheelGlow = false; }, 1800);
      },
      textChance: () => {
        this.effectTextChanceVariant = v();
        this.effectTextChance = true;
        setTimeout(() => { this.effectTextChance = false; }, 1800);
      },
    };
  }

  private fireMiniChar(t: CharType, variant: number): void {
    this.effectMiniCharVariant = variant;
    this.effectMiniChar = t;
    setTimeout(() => { if (this.effectMiniChar === t) this.effectMiniChar = null; }, 2000);
  }

  private fireCutIn(t: CharType, variant: number): void {
    this.effectCutInVariant = variant;
    this.effectCutInChar = t;
    setTimeout(() => { if (this.effectCutInChar === t) this.effectCutInChar = null; }, 1800);
  }

  scheduleEffects(
    winnerRole: 'target' | 'avoid' | undefined,
    settings: SpinEffectSettings,
    totalMs: number,
  ): void {
    // factory: 抽選後の variant を受け取って bake-in したコールバック群を返す
    this.scheduler.schedule(winnerRole, settings, totalMs, (v) => this.effectCallbacks(v));
  }

  /** 設定画面の試聴用: variant 1〜5 で固定発火、0/undefined なら 1〜5 ランダム */
  previewEffect(key: EffectKey, variant?: number): void {
    const v = (variant && variant >= 1 && variant <= 5)
      ? variant
      : 1 + Math.floor(Math.random() * 5);
    const cb = this.effectCallbacks(v)[key];
    if (cb) cb();
  }

  cancelEffects(): void {
    this.scheduler.cancel();
    this.effectFlash = false;
    this.effectTextChance = false;
    this.effectWheelGlow = false;
    this.effectMiniChar = null;
    this.effectCutInChar = null;
  }

  constructor(public id: RouletteId) {
    this.engine = new SpinEngine({
      onAngleUpdate: (a) => {
        this.wheelAngle = a;
      },
      onComplete: (_, text) => {
        const r = appState.roulettes.find((x) => x.id === this.id);
        this.winnerText = text;
        this.spinPhase = 'done';
        this.winnerVisible = true;
        if (r) {
          soundManager.setVolume(appState.preferences.volume);
          soundManager.playWin(r.settings.decideSound);
          // 履歴記録
          const cur =
            r.sets.find((s) => s.id === r.currentSetId) ?? r.sets[0];
          if (cur) {
            r.winHistory = [
              { spinAt: new Date().toISOString(), text, setId: cur.id },
              ...r.winHistory,
            ].slice(0, 100);
          }
          notifyChange();
        }
      },
      onTick: () => {
        const r = appState.roulettes.find((x) => x.id === this.id);
        if (r) soundManager.playSpin(r.settings.spinSound, 0.6);
      },
    });
  }
}

const runtimes = new Map<RouletteId, RouletteRuntime>();

/** ルーレット ID から runtime を取得 (初回はインスタンス化) */
export function rt(id: RouletteId): RouletteRuntime {
  let r = runtimes.get(id);
  if (!r) {
    r = new RouletteRuntime(id);
    runtimes.set(id, r);
  }
  return r;
}

function cleanupRuntime(id: RouletteId): void {
  const r = runtimes.get(id);
  if (r) {
    r.engine.reset();
    runtimes.delete(id);
  }
}

/** 指定ルーレットを 1 回スピン開始。idle 状態でなければ false を返す。
 *  options.suppressEffects = true で特殊演出を抑制 (グリッド表示時に使用)。
 *  特殊演出はシングル (詳細) 表示時のみ動作する仕様。 */
export function spinRoulette(id: RouletteId, options: { suppressEffects?: boolean } = {}): boolean {
  const r = appState.roulettes.find((x) => x.id === id);
  if (!r) return false;
  const runtime = rt(id);
  if (runtime.spinPhase !== 'idle') return false;

  soundManager.init(import.meta.env.BASE_URL);
  soundManager.setVolume(appState.preferences.volume);

  const set = r.sets.find((s) => s.id === r.currentSetId) ?? r.sets[0];

  // randomEachSpin: スピンごとに shuffleOrder を再生成
  if (r.settings.randomEachSpin) {
    shuffleSetForCurrent(set.id);
  }

  const layout = buildLayoutForSet(set.items, set.id, appState.ui.shuffleOrder);

  runtime.winnerVisible = false;
  runtime.winnerText = '';
  const startResult = runtime.engine.start(
    layout,
    r.settings.markerPosition,
    r.settings.markerAngleDeg,
    r.settings.spinDurationMs,
    r.settings.segmentDirection,
    r.settings.spinDirection,
    {
      durationRandomRatio: r.settings.spinDurationRandomRatio,
      phaseOverrides: r.settings.spinPhaseOverrides,
      presetProfile: r.settings.spinPresetProfile,
      phaseRandomize: r.settings.spinPhaseRandomize,
      presetRandom: r.settings.spinPresetRandom,
      durationRandom: r.settings.spinDurationRandom,
    },
  );
  if (startResult !== false) {
    runtime.spinPhase = 'spinning';
    if (!options.suppressEffects) {
      const winnerRole = set.items[startResult.winnerItemIdx]?.specialRole;
      runtime.scheduleEffects(winnerRole, r.settings.spinEffects, startResult.totalMs);
    }
    return true;
  }
  return false;
}

/** 内部: シャッフル順 (split モード) を生成して shuffleOrder にセット */
function shuffleSetForCurrent(setId: SetId): void {
  const r = activeRoulette();
  const s = r.sets.find((x) => x.id === setId);
  if (!s) return;
  const maintain = appState.preferences.randomMaintainEvenness;
  const order = generateRandomSplitOrder(s.items, maintain);
  appState.ui.shuffleOrder = { setId, mode: 'split', order };
  notifyChange();
}

/** すべての visible なルーレットを同時にスピン開始 (グリッド表示用)。
 *  'done' 状態 (結果オーバーレイ表示中) のものは dismiss してから再スピン。
 *  特殊演出はシングル表示専用なのでこちらでは抑制する。 */
export function spinAll(): void {
  for (const r of appState.roulettes) {
    if (!r.visible) continue;
    const runtime = rt(r.id);
    if (runtime.spinPhase === 'done') dismissResult(r.id);
    spinRoulette(r.id, { suppressEffects: true });
  }
}

/** 視覚演出を伴う EffectKey (プレビュー時に設定シートを半透明化する対象) */
const VISUAL_EFFECT_KEYS = new Set<EffectKey>([
  'miniCharTarget', 'miniCharExpect', 'miniCharNg',
  'cutInTarget', 'cutInExpect', 'cutInNg',
  'flashConfirm', 'wheelGlow', 'textChance',
]);

/** 設定画面で各演出を試聴するためのプレビュー発火。
 *  音系: 即時再生 (シート維持)
 *  視覚演出: 設定シートを半透明化して背後を視認可能に (約 2.4 秒)
 *  variant: 1〜5 = 固定バリエーション、省略/0 = 1〜5 ランダム */
export function previewEffect(id: RouletteId, key: EffectKey, variant?: number): void {
  const runtime = rt(id);
  if (VISUAL_EFFECT_KEYS.has(key)) {
    transientState.effectPreviewActive = true;
    setTimeout(() => { transientState.effectPreviewActive = false; }, 2400);
  }
  runtime.previewEffect(key, variant);
}

/** ルーレットの spinPhase を idle に戻す (結果オーバーレイを閉じる) */
export function dismissResult(id: RouletteId): void {
  const runtime = rt(id);
  runtime.cancelEffects();
  runtime.winnerVisible = false;
  runtime.winnerText = '';
  runtime.engine.reset();
  runtime.spinPhase = 'idle';
}

/** preferences を部分更新 (アプリ全体設定の汎用更新) */
export function updatePreferences(patch: Partial<AppPreferences>): void {
  appState.preferences = { ...appState.preferences, ...patch };
  // 音量変更は即時反映
  if (typeof patch.volume === 'number') {
    soundManager.setVolume(patch.volume);
  }
  notifyChange();
}

/** テーマモード変更 (互換ヘルパ) */
export function setThemeMode(mode: ThemeMode): void {
  if (appState.preferences.themeMode === mode) return;
  updatePreferences({ themeMode: mode });
}

/** ルーレット表示 ON/OFF (最低 1 件は visible を保つ) */
export function setRouletteVisible(id: RouletteId, visible: boolean): void {
  const r = appState.roulettes.find((x) => x.id === id);
  if (!r) return;
  if (!visible) {
    const visibleCount = appState.roulettes.filter((x) => x.visible).length;
    if (visibleCount <= 1 && r.visible) return;
  }
  r.visible = visible;
  notifyChange();
}

export function setActiveRoulette(id: RouletteId): void {
  if (!appState.roulettes.some((r) => r.id === id)) return;
  if (appState.activeRouletteId === id) return;
  appState.activeRouletteId = id;
  // ルーレット切替時は一時シャッフル順をクリア
  appState.ui.shuffleOrder = null;
  notifyChange();
}

// ─────────────────────────────────────────────────────────
//  ルーレット CRUD
// ─────────────────────────────────────────────────────────

/** 新規ルーレットを追加してアクティブに切替。新規 id を返す */
export function addRoulette(name: string): RouletteId {
  const trimmed = name.trim() || `ルーレット ${appState.roulettes.length + 1}`;
  const r = makeRoulette(trimmed);
  appState.roulettes = [...appState.roulettes, r];
  appState.activeRouletteId = r.id;
  appState.ui.shuffleOrder = null;
  notifyChange();
  return r.id;
}

export function renameRoulette(id: RouletteId, name: string): void {
  const r = appState.roulettes.find((x) => x.id === id);
  const trimmed = name.trim();
  if (!r || !trimmed) return;
  r.name = trimmed;
  notifyChange();
}

/** ルーレットを複製。新しい id を返す (アクティブにはしない、表示状態と設定はコピー) */
export function duplicateRoulette(id: RouletteId): RouletteId | null {
  const orig = appState.roulettes.find((r) => r.id === id);
  if (!orig) return null;
  // 新セット ID マップを作る
  const newSets = orig.sets.map((s) => ({
    id: uuid(),
    name: s.name,
    items: s.items.map((it) => ({ ...it })),
  }));
  // 元 currentSetId に対応する新 set 取得
  const origCurIdx = orig.sets.findIndex((s) => s.id === orig.currentSetId);
  const newCurrentSetId =
    newSets[origCurIdx >= 0 ? origCurIdx : 0]?.id ?? newSets[0].id;
  const cloned: Roulette = {
    id: uuid(),
    name: `${orig.name} (コピー)`,
    sets: newSets,
    currentSetId: newCurrentSetId,
    settings: { ...orig.settings },
    winHistory: [], // 履歴はコピーしない (新しいルーレットなので別カウント)
    visible: true,
  };
  appState.roulettes = [...appState.roulettes, cloned];
  notifyChange();
  return cloned.id;
}

/** ルーレット削除。最後の 1 件は削除しない (invariant)。 */
export function deleteRoulette(id: RouletteId): void {
  if (appState.roulettes.length <= 1) return;
  appState.roulettes = appState.roulettes.filter((r) => r.id !== id);
  if (appState.activeRouletteId === id) {
    appState.activeRouletteId = appState.roulettes[0].id;
  }
  appState.ui.shuffleOrder = null;
  cleanupRuntime(id);
  notifyChange();
}

export function currentSet(): ItemSet {
  const r = activeRoulette();
  return r.sets.find((s) => s.id === r.currentSetId) ?? r.sets[0];
}

// ─────────────────────────────────────────────────────────
//  セット CRUD
// ─────────────────────────────────────────────────────────

export function setCurrentSet(setId: SetId): void {
  const r = activeRoulette();
  if (!r.sets.some((s) => s.id === setId)) return;
  if (r.currentSetId === setId) return;
  r.currentSetId = setId;
  // 別セットへの切替時は一時シャッフル順をクリア
  appState.ui.shuffleOrder = null;
  notifyChange();
}

/** 新規セットを追加してアクティブに切り替える。新セットの id を返す。 */
export function addSet(name: string): SetId {
  const r = activeRoulette();
  const trimmed = name.trim() || `セット ${r.sets.length + 1}`;
  const newSet = makeSet(trimmed);
  r.sets.push(newSet);
  r.currentSetId = newSet.id;
  notifyChange();
  return newSet.id;
}

export function renameSet(setId: SetId, name: string): void {
  const r = activeRoulette();
  const s = r.sets.find((x) => x.id === setId);
  const trimmed = name.trim();
  if (!s || !trimmed) return;
  s.name = trimmed;
  notifyChange();
}

/** セットを削除。最後の 1 件は削除しない (invariant)。 */
export function deleteSet(setId: SetId): void {
  const r = activeRoulette();
  if (r.sets.length <= 1) return;
  r.sets = r.sets.filter((s) => s.id !== setId);
  if (r.currentSetId === setId) {
    r.currentSetId = r.sets[0].id;
  }
  notifyChange();
}

// ─────────────────────────────────────────────────────────
//  項目 CRUD
// ─────────────────────────────────────────────────────────

/** セット内の項目配列を一括更新 (バルクテキスト編集用)。空配列はデフォルト 1 項目に補正。
 *  項目順が変わるためシャッフル順 (shuffleOrder) はクリアする。 */
export function replaceItems(setId: SetId, items: Item[]): void {
  const r = activeRoulette();
  const s = r.sets.find((x) => x.id === setId);
  if (!s) return;
  s.items = items.length > 0 ? items : [makeItem('項目 1')];
  appState.ui.shuffleOrder = null;
  notifyChange();
}

/** 1 項目を更新 */
export function updateItem(setId: SetId, idx: number, item: Item): void {
  const r = activeRoulette();
  const s = r.sets.find((x) => x.id === setId);
  if (!s || idx < 0 || idx >= s.items.length) return;
  s.items[idx] = item;
  notifyChange();
}

/** 現在のルーレットの勝利履歴を全削除 */
export function clearWinHistory(): void {
  const r = activeRoulette();
  r.winHistory = [];
  notifyChange();
}

/** 項目を 1 つ上 (-1) または下 (+1) に移動する。
 *  順序が変わるためシャッフル順 (shuffleOrder) はクリア。 */
export function moveItem(setId: SetId, idx: number, dir: -1 | 1): void {
  const r = activeRoulette();
  const s = r.sets.find((x) => x.id === setId);
  if (!s) return;
  const newIdx = idx + dir;
  if (newIdx < 0 || newIdx >= s.items.length) return;
  const arr = [...s.items];
  [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
  s.items = arr;
  appState.ui.shuffleOrder = null;
  notifyChange();
}

/** 「今すぐシャッフル」: 項目本体は不変。preferences.randomMaintainEvenness で
 *  - true:  'item' モード (項目順だけ並び替え、splits は均等維持)
 *  - false: 'split' モード (各 split を独立にランダム配置, Windows 同等)
 *  元の順序に戻すには restoreOrder() を呼ぶ。 */
export function shuffleCurrentItems(setId: SetId): void {
  shuffleSetForCurrent(setId);
}

/** シャッフル前の標準順に戻す (shuffleOrder をクリア)。 */
export function restoreOrder(): void {
  if (appState.ui.shuffleOrder !== null) {
    appState.ui.shuffleOrder = null;
    notifyChange();
  }
}

/** 現在 shuffleOrder が適用中かどうか (currentSet と一致しているか)。 */
export function hasShuffleOrder(setId: SetId): boolean {
  const so = appState.ui.shuffleOrder;
  return so !== null && so.setId === setId;
}

/** 項目 ON/OFF。最後の ON 項目は OFF にできない。 */
export function toggleItemEnabled(setId: SetId, idx: number): void {
  const r = activeRoulette();
  const s = r.sets.find((x) => x.id === setId);
  if (!s || idx < 0 || idx >= s.items.length) return;
  const onCount = s.items.filter((it) => it.enabled).length;
  if (s.items[idx].enabled && onCount <= 1) return;
  s.items[idx].enabled = !s.items[idx].enabled;
  notifyChange();
}
